// E8CubePyramidVolumeRatioOpenTask.js
// Zadanie otwarte E8 (2026 zad. 18) - ostroslup ACDS w szescianie.

import React from "react";
import { BlockMath, InlineMath, ReadableInlineMath } from "../../../../components/math/KatexWrappers";
import SvgMathLabel from "../../../../components/diagrams/SvgMathLabel";
import EquationInput from "../../../../components/inputs/EquationInput";
import FractionInput from "../../../../components/inputs/FractionInput";
import { scoreStaged } from "../../../openTaskScoring";

function rawStr(v) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v.raw === "string") return v.raw;
  if (typeof v.value === "string") return v.value;
  return "";
}

const POSITION_VARIANTS = [
  { numerator: 1, denominator: 2 },
  { numerator: 1, denominator: 3 },
  { numerator: 1, denominator: 4 },
  { numerator: 2, denominator: 3 },
  { numerator: 3, denominator: 4 },
];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function fractionLatex(numerator, denominator) {
  if (numerator === denominator) return "1";
  if (numerator === 1) return "\\frac{1}{" + denominator + "}";
  return "\\frac{" + numerator + "}{" + denominator + "}";
}

function positionText(numerator, denominator) {
  if (numerator === 1 && denominator === 2) return "\u015brodkiem kraw\u0119dzi";
  return "punktem kraw\u0119dzi";
}

function buildData({ edgeLabel = "a", heightNumerator = 1, heightDenominator = 2 } = {}) {
  const ratio = (6 * heightDenominator) / heightNumerator;
  return {
    edgeLabel,
    heightNumerator,
    heightDenominator,
    heightFractionLatex: fractionLatex(heightNumerator, heightDenominator),
    positionText: positionText(heightNumerator, heightDenominator),
    ratio,
    maxPoints: 2,
  };
}

function createOriginalTask() {
  return {
    type: "E8CubePyramidVolumeRatioOpenTask",
    data: buildData(),
    source: { variant: "original", exam: "E8", session: "2026_MAJ", taskNumber: 18 },
    tags: ["e8", "stereometria", "szescian", "ostroslup", "objetosc", "zadanie-otwarte"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  const variant = pick(POSITION_VARIANTS);
  return {
    type: "E8CubePyramidVolumeRatioOpenTask",
    data: buildData({
      heightNumerator: variant.numerator,
      heightDenominator: variant.denominator,
    }),
    source: { variant: "random", basedOn: "E8_2026_MAJ_18" },
    tags: ["e8", "stereometria", "szescian", "ostroslup", "objetosc", "zadanie-otwarte"],
  };
}

function normalize(text) {
  return rawStr(text).toLowerCase().replace(/\s+/g, "");
}

function parseNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value && typeof value === "object" && Number.isFinite(value.parsed)) return value.parsed;
  const raw = rawStr(value);
  const direct = Number(raw.replace?.(",", "."));
  if (Number.isFinite(direct)) return direct;
  const match = normalize(raw).match(/\d+(?:[.,]\d+)?/);
  return match ? Number(match[0].replace(",", ".")) : null;
}

function CubeSketch({ data }) {
  const accent = "#ff9f0a";
  const fill = "rgba(255,159,10,0.12)";
  const A = { x: 78, y: 170 }, B = { x: 178, y: 170 }, C = { x: 224, y: 130 }, D = { x: 124, y: 130 };
  const E = { x: 78, y: 70 }, F = { x: 178, y: 70 }, G = { x: 224, y: 30 }, H = { x: 124, y: 30 };
  const t = data.heightNumerator / data.heightDenominator;
  const S = { x: D.x, y: D.y - (D.y - H.y) * t };
  const labels = [["A",A,-14,18],["B",B,14,18],["C",C,16,4],["D",D,12,-10],["E",E,-16,-8],["F",F,16,14],["G",G,16,-8],["H",H,-16,-8],["S",S,-18,16]];
  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "14px 0" }}>
      <svg width="310" height="210" viewBox="0 0 310 210" role="img" aria-label="Sześcian ABCDEFGH z ostrosłupem ACDS">
        {/* Ukryte krawędzie — kreskowane */}
        <line x1={C.x} y1={C.y} x2={D.x} y2={D.y} stroke="currentColor" strokeWidth="1.5" strokeDasharray="5,4" />
        <line x1={D.x} y1={D.y} x2={A.x} y2={A.y} stroke="currentColor" strokeWidth="1.5" strokeDasharray="5,4" />
        <line x1={D.x} y1={D.y} x2={H.x} y2={H.y} stroke="currentColor" strokeWidth="1.5" strokeDasharray="5,4" />
        {/* Wypełnienia widocznych ścian */}
        <polygon points={`${A.x},${A.y} ${B.x},${B.y} ${F.x},${F.y} ${E.x},${E.y}`} fill={fill} stroke="none" />
        <polygon points={`${B.x},${B.y} ${C.x},${C.y} ${G.x},${G.y} ${F.x},${F.y}`} fill={fill} stroke="none" />
        <polygon points={`${E.x},${E.y} ${F.x},${F.y} ${G.x},${G.y} ${H.x},${H.y}`} fill={fill} stroke="none" />
        {/* Widoczne krawędzie — pełne */}
        <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke="currentColor" strokeWidth="2" />
        <line x1={A.x} y1={A.y} x2={E.x} y2={E.y} stroke="currentColor" strokeWidth="2" />
        <line x1={B.x} y1={B.y} x2={C.x} y2={C.y} stroke="currentColor" strokeWidth="2" />
        <line x1={B.x} y1={B.y} x2={F.x} y2={F.y} stroke="currentColor" strokeWidth="2" />
        <line x1={C.x} y1={C.y} x2={G.x} y2={G.y} stroke="currentColor" strokeWidth="2" />
        <line x1={E.x} y1={E.y} x2={F.x} y2={F.y} stroke="currentColor" strokeWidth="2" />
        <line x1={E.x} y1={E.y} x2={H.x} y2={H.y} stroke="currentColor" strokeWidth="2" />
        <line x1={F.x} y1={F.y} x2={G.x} y2={G.y} stroke="currentColor" strokeWidth="2" />
        <line x1={G.x} y1={G.y} x2={H.x} y2={H.y} stroke="currentColor" strokeWidth="2" />
        {/* Ostrosłup ACDS — wypełnienie widocznych ścian (bryła) */}
        <polygon points={`${A.x},${A.y} ${S.x},${S.y} ${C.x},${C.y}`} fill="rgba(120,200,170,0.32)" stroke="none" />
        <polygon points={`${A.x},${A.y} ${S.x},${S.y} ${D.x},${D.y}`} fill="rgba(120,200,170,0.22)" stroke="none" />
        <polygon points={`${A.x},${A.y} ${D.x},${D.y} ${C.x},${C.y}`} fill="rgba(120,200,170,0.18)" stroke="none" />
        {/* Krawędzie ostrosłupa */}
        <line x1={A.x} y1={A.y} x2={S.x} y2={S.y} stroke={accent} strokeWidth="2.5" />
        <line x1={S.x} y1={S.y} x2={C.x} y2={C.y} stroke={accent} strokeWidth="2.5" />
        <line x1={A.x} y1={A.y} x2={C.x} y2={C.y} stroke={accent} strokeWidth="2.5" />
        <line x1={S.x} y1={S.y} x2={D.x} y2={D.y} stroke={accent} strokeWidth="2.5" />
        <line x1={A.x} y1={A.y} x2={D.x} y2={D.y} stroke={accent} strokeWidth="1.5" strokeDasharray="5,4" />
        <line x1={D.x} y1={D.y} x2={C.x} y2={C.y} stroke={accent} strokeWidth="1.5" strokeDasharray="5,4" />
        <circle cx={S.x} cy={S.y} r="4" fill={accent} />
        {labels.map(([label, p, dx, dy]) => (
          <SvgMathLabel key={label} x={p.x + dx} y={p.y + dy} math={label} width={22} height={22} fontSize={16} />
        ))}
      </svg>
    </div>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        {"Dany jest sze\u015bcian "}<InlineMath math="ABCDEFGH" />{" o kraw\u0119dzi d\u0142ugo\u015bci "}<InlineMath math="a" />{". "}
        {data.heightNumerator === 1 && data.heightDenominator === 2 ? (
          <>
            {"Punkt "}<InlineMath math="S" />{" jest "}{data.positionText}{" "}<InlineMath math="DH" />{" tego sze\u015bcianu. "}
          </>
        ) : (
          <>
            {"Punkt "}<InlineMath math="S" />{" jest "}{data.positionText}{" "}<InlineMath math="DH" />
            {", dla kt\u00f3rego "}<ReadableInlineMath math={`DS=${data.heightFractionLatex}\\,DH`} verticalAlign="-0.18em" />{". "}
          </>
        )}
        {"Punkty "}<InlineMath math="A,C,D,S" />{" s\u0105 wierzcho\u0142kami ostros\u0142upa tr\u00f3jk\u0105tnego."}
      </p>
      <CubeSketch data={data} />
      <p>{"Oblicz, ile razy obj\u0119to\u015b\u0107 ostros\u0142upa "}<InlineMath math="ACDS" />{" jest mniejsza od obj\u0119to\u015bci sze\u015bcianu "}<InlineMath math="ABCDEFGH" />{". Zapisz obliczenia."}</p>
    </div>
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const ua = userAnswer || {};
  const set = (field) => (val) => onChange?.({ ...ua, [field]: val });
  return (
    <div style={{ display: "grid", gap: 14, textAlign: "left" }}>
      <div
        style={{
          fontSize: 12.5,
          opacity: 0.78,
          lineHeight: 1.45,
          borderLeft: "3px solid var(--color-accent)",
          paddingLeft: 8,
        }}
      >
        <strong>Obliczenia (opcjonalne).</strong> Wypełnij je, jeśli chcesz dostać punkt
        cząstkowy, gdyby ostateczny wynik był nieprawidłowy. Możesz zapisać w dowolnej
        formie (bez indeksów dolnych).
      </div>

      <div>
        <div style={{ marginBottom: 4, fontWeight: 600 }}>
          Objętość ostrosłupa ACDS (w zależności od a):
        </div>
        <EquationInput
          value={ua.pyramidVolume || ""}
          onChange={set("pyramidVolume")}
          disabled={disabled}
          placeholder="np. 1/3 · 1/2 a^2 · ... = ... a^3"
          basis="360px"
        />
      </div>

      <div>
        <div style={{ marginBottom: 4, fontWeight: 600 }}>
          Ile razy mniejsza jest objętość ostrosłupa od objętości sześcianu?{" "}
          <span style={{ fontWeight: 400, opacity: 0.7 }}>(ostateczny wynik)</span>
        </div>
        <FractionInput
          value={ua.ratio || ""}
          onChange={set("ratio")}
          disabled={disabled}
          label=""
          suffixLatex="\\ \\text{razy}"
          placeholder="np. liczba całkowita"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const resultOk = parseNumber(userAnswer.ratio) === data.ratio;

  // Krok cząstkowy (1 pkt): poprawna objętość ostrosłupa w zależności od a.
  // Akceptujemy gotowy wynik (1/ratio a^3) albo poprawny tok (1/3 · 1/2 · wysokość),
  // w dowolnym zapisie (bez wymogu indeksów dolnych).
  const vol = normalize(userAnswer.pyramidVolume);
  const heightFraction = `${data.heightNumerator}/${data.heightDenominator}`;
  const heightDecimal = String(data.heightNumerator / data.heightDenominator); // np. "0.75"
  const hasFinalVolume = vol.includes(`1/${data.ratio}`);
  const hasMethod =
    vol.includes("1/3") &&
    (vol.includes("1/2") || vol.includes("0.5")) &&
    (vol.includes(heightFraction) || vol.includes(heightDecimal));
  const volumeOk = hasFinalVolume || hasMethod;

  return scoreStaged({
    resultOk,
    milestones: [volumeOk],
    maxScore: data.maxPoints,
    parts: { "objętość ostrosłupa": volumeOk, "stosunek": resultOk },
  });
}

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>{"Przyk\u0142adowa poprawna odpowied\u017a:"}</div>
      <BlockMath math="V_s=a^3" />
      <BlockMath math={`V_o=\\frac{1}{3}\\cdot\\frac{1}{2}a^2\\cdot${data.heightFractionLatex}a=\\frac{1}{${data.ratio}}a^3`} />
      <div>{"Odpowied\u017a: obj\u0119to\u015b\u0107 ostros\u0142upa jest "}<InlineMath math={`${data.ratio}`} />{" razy mniejsza od obj\u0119to\u015bci sze\u015bcianu."}</div>
    </div>
  );
}

export function renderUserAnswer(userAnswer = {}) {
  const vol = rawStr(userAnswer.pyramidVolume);
  const ratio = rawStr(userAnswer.ratio);
  if (!vol && !ratio) return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ textAlign: "left", display: "grid", gap: 4 }}>
      <div>{"Obj\u0119to\u015b\u0107 ostros\u0142upa: "}{vol || <i>(brak)</i>}</div>
      <div>{"Ile razy mniejsza obj\u0119to\u015b\u0107: "}<InlineMath math={`${ratio || "--"}\\ \\text{razy}`} /></div>
    </div>
  );
}
export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> {"Obj\u0119to\u015b\u0107 sze\u015bcianu:"}</p>
      <BlockMath math="V_s=a^3" />
      <p>
        <strong>Krok 2.</strong> {"Podstawa ostros\u0142upa "}<InlineMath math="ACD" />{" jest po\u0142ow\u0105 kwadratu "}<InlineMath math="ABCD" />{","}
        {" wi\u0119c ma pole "}<InlineMath math={"\\frac{1}{2}a^2"} />{". Wysoko\u015b\u0107 "}<InlineMath math="DS" />{" jest r\u00f3wna"}
        <InlineMath math={`${data.heightFractionLatex}a`} />.
      </p>
      <BlockMath math={`V_o=\\frac{1}{3}\\cdot\\frac{1}{2}a^2\\cdot${data.heightFractionLatex}a=\\frac{1}{${data.ratio}}a^3`} />
      <p>{"Zatem obj\u0119to\u015b\u0107 ostros\u0142upa jest "}<InlineMath math={`${data.ratio}`} />{" razy mniejsza od obj\u0119to\u015bci sze\u015bcianu."}</p>
    </div>
  );
}
